import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { StyledTabs } from "core/components/StyledTabs";
import { type SyntheticEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const MENU_TAB_VALUE = "menu";

export interface LanguageTabsProps<T extends string | number = string> {
  handleTabChange: (event: SyntheticEvent, value: T) => void;
  isTabDefault?: boolean;
  currentTabs: { label: string; value: T }[];
  tabValue: T;
  unselectedTabs: { label: string; value: T }[];
  removeTabOnClick: (tabValue: T) => void;
  selectAddTabValue: (tabValue: T) => void;
  tabVariant?: "styled" | "standard";
}

export function LanguageTabs<T extends string>({
  handleTabChange,
  tabValue,
  isTabDefault,
  currentTabs,
  unselectedTabs,
  removeTabOnClick,
  selectAddTabValue,
  tabVariant = "styled",
}: LanguageTabsProps<T>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("Fixhub");
  const removeTabVisible =
    !isTabDefault && currentTabs.length > 1 && Boolean(tabValue);
  const TabsComponent = tabVariant === "standard" ? Tabs : StyledTabs;
  return (
    <Box width="100%">
      <Menu
        id="additional-language-menu"
        // eslint-disable-next-line react-hooks/refs
        anchorEl={ref.current}
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
        }}
      >
        {unselectedTabs.map(({ label, value }) => (
          <MenuItem
            key={value}
            onClick={(e) => {
              selectAddTabValue(value);
              setMenuOpen(false);
              handleTabChange(e, value);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
      <Stack direction="row">
        <TabsComponent
          value={tabValue}
          onChange={(event, value) => {
            if (value === MENU_TAB_VALUE) {
              setMenuOpen(true);
              return;
            }
            handleTabChange(event, value);
          }}
        >
          {currentTabs.map(({ label, value }) => (
            <Tab key={value} label={label} value={value} />
          ))}
          {unselectedTabs.length > 0 && (
            <Tab
              value={MENU_TAB_VALUE}
              ref={ref}
              label={<AddIcon />}
              sx={{
                borderRadius: ({ shape: { borderRadius: br } }) =>
                  `${br}px ${br}px 0 0`,
                "&:hover": {
                  backgroundColor: ({ palette }) => palette.action.hover,
                },
              }}
            />
          )}
        </TabsComponent>
        <Fade in={removeTabVisible}>
          <Stack direction="row" flex={1} justifyContent="flex-end">
            <Button
              color="error"
              onClick={() => {
                removeTabOnClick(tabValue);
              }}
            >
              {t("Next:Core:LanguageTabs.RemoveLanguage")}
            </Button>
          </Stack>
        </Fade>
      </Stack>
      <Divider />
    </Box>
  );
}
